/* Serper (Google) vault — API-key + zoek-probe. */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Link2, CheckCircle2, Loader2, Eye, EyeOff, Search } from "lucide-react";
import { SubSection } from "./VaultBits";

/* ----------------------------------------------------------------- */
/*  Serper vault — api_key + ping + dry-run search                    */
/* ----------------------------------------------------------------- */
function SerperVault({ cfg, onSaved }) {
    const { t } = useTranslation();
    const [apiKey, setApiKey] = useState("");
    const [reveal, setReveal] = useState(false);
    const [busy, setBusy] = useState(false);
    const [pingBusy, setPingBusy] = useState(false);
    const [pingResult, setPingResult] = useState(null);
    const [probeBusy, setProbeBusy] = useState(false);
    const [probeQ, setProbeQ] = useState("Belgian SaaS Series A 2026");
    const [probeEp, setProbeEp] = useState("search");
    const [probeResult, setProbeResult] = useState(null);

    const save = async () => {
        if (!apiKey.trim() && !cfg.configured) { toast.error(t("integr.enter_key")); return; }
        setBusy(true);
        try {
            await api.patch("/integrations/serper", { api_key: apiKey });
            toast.success(t("integr.key_saved", { name: "Serper" }));
            setApiKey(""); setReveal(false); onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || t("integr.save_failed")); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(t("integr.delete_key_confirm", { name: "Serper" }))) return;
        setBusy(true);
        try { await api.delete("/integrations/serper"); toast.success(t("integr.deleted")); setPingResult(null); setProbeResult(null); onSaved(); }
        catch (e) { toast.error(e?.response?.data?.detail || t("integr.delete_failed")); }
        finally { setBusy(false); }
    };
    const testPing = async () => {
        setPingBusy(true);
        try {
            const r = await api.get("/integrations/serper/test");
            setPingResult(r.data);
            toast.success(t("integr.serper_valid", { hits: r.data.hits, ms: r.data.latency_ms }));
        } catch (e) { setPingResult(null); toast.error(e?.response?.data?.detail || t("integr.test_failed")); }
        finally { setPingBusy(false); }
    };
    const runProbe = async () => {
        setProbeBusy(true);
        try {
            const r = await api.post("/integrations/serper/probe", { query: probeQ, endpoint: probeEp });
            setProbeResult(r.data.hits || []);
            toast.success(t("integr.serper_probe_done", { n: r.data.count, ep: probeEp }));
        } catch (e) { setProbeResult(null); toast.error(e?.response?.data?.detail || t("integr.probe_failed")); }
        finally { setProbeBusy(false); }
    };
    const hostOf = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };

    return (
        <div className="space-y-4">
            <SubSection title={`1. ${t("integr.sec_api_key")}`} testid="serper-credentials">
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Serper.dev API Key</label>
                <div className="mt-1.5 flex gap-2">
                    <div className="flex-1 relative">
                        <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                        <input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                            placeholder={cfg.api_key_masked || t("integr.serper_key_ph")}
                            type={reveal ? "text" : "password"}
                            data-testid="serper-apikey-input"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg pl-9 pr-10 py-2.5 text-sm text-white outline-none focus:border-[#22c55e]" />
                        <button type="button" onClick={() => setReveal((r) => !r)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--jr-text-3)] hover:text-white">
                            {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                    </div>
                    <button onClick={save} disabled={busy} data-testid="serper-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#22c55e] text-black text-sm font-medium hover:bg-[#22c55e]/85 disabled:opacity-50">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {t("common.save")}
                    </button>
                </div>
                <p className="mt-2 text-[10px] text-[var(--jr-text-3)]">
                    {t("integr.serper_where")} <a href="https://serper.dev" target="_blank" rel="noreferrer" className="text-[var(--jr-text-2)] underline">serper.dev</a> → API Keys.
                    {" "}{t("integr.serper_pricing")}
                </p>
                {cfg.configured && (
                    <button onClick={remove} className="mt-2 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-[#ef4444]">
                        {t("integr.delete_current_key")}
                    </button>
                )}
            </SubSection>

            <SubSection title={`2. ${t("integr.sec_test_conn")}`}
                action={
                    <button onClick={testPing} disabled={pingBusy || !cfg.configured} data-testid="serper-test"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] bg-[#22c55e]/15 text-[#22c55e] border border-[#22c55e]/30 hover:bg-[#22c55e]/25 disabled:opacity-50">
                        {pingBusy ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />} Ping /search
                    </button>
                }
                testid="serper-test-section">
                {!pingResult ? (
                    <p className="text-xs text-[var(--jr-text-3)] italic">{t("integr.serper_ping_hint")}</p>
                ) : (
                    <div className="rounded-lg border border-[#22c55e]/25 bg-[#22c55e]/[0.06] p-3 text-xs text-[var(--jr-text-2)] inline-flex items-start gap-2"
                        data-testid="serper-ping-result">
                        <CheckCircle2 size={14} className="text-[#22c55e] mt-0.5 shrink-0" />
                        <div>
                            <div className="text-white">Serper /search · {pingResult.hits} hits · {pingResult.latency_ms}ms</div>
                            <div className="text-[var(--jr-text-3)]">{t("integr.serper_ping_ok")}</div>
                        </div>
                    </div>
                )}
            </SubSection>

            <SubSection title={`3. ${t("integr.serper_probe_title")}`} testid="serper-probe-section">
                <p className="text-[11px] text-[var(--jr-text-3)] mb-2">
                    {t("integr.serper_probe_sub")} <span className="text-[var(--jr-amber)]">{t("integr.serper_probe_cost")}</span>
                </p>
                <div className="grid grid-cols-4 gap-2">
                    <input value={probeQ} onChange={(e) => setProbeQ(e.target.value)}
                        placeholder={t("integr.query_ph")}
                        className="col-span-3 bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-xs text-white outline-none focus:border-[#22c55e]"
                        data-testid="serper-probe-query"/>
                    <select value={probeEp} onChange={(e) => setProbeEp(e.target.value)}
                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-2 py-2 text-xs text-white outline-none focus:border-[#22c55e]"
                        data-testid="serper-probe-endpoint">
                        <option value="search">/search</option>
                        <option value="news">/news</option>
                        <option value="jobs">/jobs</option>
                    </select>
                </div>
                <button onClick={runProbe} disabled={probeBusy || !cfg.configured} data-testid="serper-probe-run"
                    className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#22c55e] text-black text-xs font-medium hover:bg-[#22c55e]/85 disabled:opacity-50">
                    {probeBusy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} {t("integr.run_probe")}
                </button>
                {probeResult && probeResult.length > 0 && (
                    <ul className="mt-3 space-y-2" data-testid="serper-probe-result">
                        {probeResult.slice(0, 5).map((h, i) => (
                            <li key={i} className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)]/40 p-3 text-xs">
                                <div className="text-white font-medium truncate">{h.title || h.name || t("integr.untitled")}</div>
                                {h.snippet && <p className="mt-1 text-[11px] text-[var(--jr-text-3)] line-clamp-2">{h.snippet}</p>}
                                {h.link && (
                                    <a href={h.link} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[10px] text-[var(--jr-info)] hover:underline">
                                        <Link2 size={9} /> {hostOf(h.link)}
                                    </a>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </SubSection>
        </div>
    );
}

export default SerperVault;
